import { useAppDataPrefetcher } from "@/api/prefetch";
import { DemoBanner } from "@/components/layout/demo-banner";
import { ResponsiveShell } from "@/components/layout/responsive-shell";
import { LoadingScreen } from "@/components/loading-screen";
import { SplashBridge } from "@/components/splash-bridge";
import { ConfirmationProvider } from "@/components/ui/confirmation-dialog";
import { ToastProvider } from "@/components/ui/toast";
import { VerifiedUserGates } from "@/features/auth/verified-user-gates";
import { TourTargetProvider } from "@/features/feature-tours";
import { getLandingRoute } from "@/features/settings/landing";
import { AppGateProvider, useAppGate } from "@/features/setup/gate/use-app-gate";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useAuthStatus } from "@/hooks/use-auth-status";
import { useNotificationWebSocket } from "@/hooks/use-notification-websocket";
import { useUnifiedColorScheme } from "@/hooks/use-unified-color-scheme";
import { useQueryClient } from "@tanstack/react-query";
import '@/lib/nativewind-interop';
import { LocationProvider } from "@/providers/location-provider";
import { QueryProvider } from "@/providers/query-provider";
import { ResponsiveShellProvider } from "@/providers/responsive-shell-provider";
import { UnifiedThemeProvider, useAppTheme } from "@/providers/theme-provider";
import Ionicons from "@expo/vector-icons/Ionicons";
import {
  HankenGrotesk_400Regular,
  HankenGrotesk_500Medium,
  HankenGrotesk_600SemiBold,
  HankenGrotesk_700Bold,
} from "@expo-google-fonts/hanken-grotesk";
import { useFonts } from "expo-font";
import { Stack, usePathname, useRouter } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import React, { useEffect, useRef } from "react";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { SafeAreaProvider, initialWindowMetrics } from "react-native-safe-area-context";
import "../../global.css";

import { wrap as sentryWrap } from '@/utils/sentry';

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

// Crossfade the native splash out into the JS overlay rather than hard-cutting.
// The fade is what absorbs Android 12+'s circle-masked, system-sized icon not
// pixel-matching our overlay. (`fade` is iOS-only; `duration` applies to both.)
SplashScreen.setOptions({ fade: true, duration: 250 });

// Component to handle prefetching when auth becomes verified
function PrefetchOnVerified() {
  const authStatus = useAuthStatus();
  const { user } = useAuth();
  const prefetcher = useAppDataPrefetcher();
  const hasPrefetchedRef = useRef(false);

  useEffect(() => {
    // Prefetch essential data when auth transitions to verified
    if (authStatus === 'verified' && user?.id && !hasPrefetchedRef.current) {
      hasPrefetchedRef.current = true;
      prefetcher.prefetchEssentialData(user.id).catch((error) => {
        console.warn('Prefetch failed:', error);
        // Don't block app initialization on prefetch failures
      });
    }

    // Reset prefetch flag when user logs out
    if (authStatus === 'loggedOut') {
      hasPrefetchedRef.current = false;
    }
  }, [authStatus, user?.id, prefetcher]);

  return null;
}

// Centralized route protection component
function ProtectedRoutes() {
  const authStatus = useAuthStatus();
  // The single owner of "what must the user finish before the app opens".
  // Re-deriving it here from auth plus a query is how ADR 0006's ordering bugs
  // got in, so this component only ever reads the answer.
  const { gate, isBooting } = useAppGate();
  const { user } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const theme = useAppTheme();
  useNotificationWebSocket();
  const hasResolvedOnceRef = React.useRef(false);

  // Track when auth has resolved at least once (initial boot complete)
  React.useEffect(() => {
    if (authStatus !== 'loading') {
      hasResolvedOnceRef.current = true;
    }
  }, [authStatus]);

  // Handle unverified users - always redirect to verify-otp
  useEffect(() => {
    if (authStatus === 'unverified' && user?.email) {
      const currentPath = pathname || '';
      if (!currentPath.includes('verify-otp')) {
        const email = user.email || '';
        router.replace({
          pathname: '/(auth)/verify-otp',
          params: { email }
        });
      }
    }
  }, [authStatus, user?.email, pathname, router]);

  // Handle users who ended up on auth routes with the app open to them.
  // Keyed on the gate, not on `verified`: a verified user still in setup must
  // not be sent to a landing route whose group is not mounted.
  useEffect(() => {
    if (gate === 'app') {
      const currentPath = pathname || '';
      if (currentPath.includes('/(auth)')) {
        // Honor the persisted default landing screen (synchronous cache read).
        router.replace({ pathname: getLandingRoute(queryClient) });
      }
    }
  }, [gate, pathname, router, queryClient]);

  // Handle logged-out users trying to access protected routes - redirect to login
  // IMPORTANT: Never redirect if already on an auth route (preserves register/login state)
  useEffect(() => {
    if (authStatus === 'loggedOut') {
      const currentPath = pathname || '';
      const isOnAuthRoute = currentPath.includes('/(auth)');

      if (!isOnAuthRoute && (currentPath.startsWith('/(tabs)') || currentPath.startsWith('/(root)'))) {
        router.replace({ pathname: '/(auth)/login' });
      }
    }
  }, [authStatus, pathname, router]);

  // Cold boot: nothing is decided yet, so mount nothing. `isBooting` also covers
  // the eligibility read, which is what stops the app shell painting a frame
  // before a setup screen takes over.
  if (isBooting) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // After the first resolve, a `loading` blip (tab refocus, session revalidate)
  // must not remount the Stack and lose navigation state. The ref is read during
  // render on purpose, and only ever matters on a render that follows a
  // committed non-loading one, so the committed value is the current value.
  /* eslint-disable-next-line react-hooks/refs -- see above; mirrors use-app-gate.tsx */
  const isLoadingBlip = authStatus === 'loading' && hasResolvedOnceRef.current;

  // After initial boot, always render Stack to preserve navigation state
  // Even if authStatus becomes 'loading' again (e.g., on tab refocus), keep Stack mounted
  return (
    <>
      {/* Trigger prefetch when auth becomes verified */}
      {authStatus === 'verified' && <PrefetchOnVerified />}
      {/* The only two surfaces still allowed to paint over the app: policy
          acceptance (blocking, can hit an existing user mid-session) and the
          feature tour layer, which gates itself on `gate === 'app'`. */}
      {authStatus === 'verified' && <VerifiedUserGates />}

      {/* ResponsiveShellProvider manages drawer state and responsive breakpoints */}
      <ResponsiveShellProvider>
        {/* ResponsiveShell renders sidebar (desktop) or drawer (mobile) for authenticated screens */}
        <ResponsiveShell>
          {/* Demo mode banner — app chrome, so it waits for the app tier rather
              than for raw `verified`, which is true while `(setup)` still owns
              the screen (ADR 0006). */}
          {gate === 'app' && <DemoBanner />}
          {/* Always render Stack to preserve navigation state */}
          <Stack
            screenOptions={{
              headerShown: false,
              contentStyle: { backgroundColor: theme.colors.background }
            }}
          >
            {/* The root index redirects a verified user to their landing route,
                which is not mounted during setup. Guarding it lets the router
                fall through to the only available screen, `(setup)`, instead of
                stranding the user on a replace that no navigator can handle. */}
            <Stack.Protected guard={gate !== 'setup'}>
              <Stack.Screen name="index" />
            </Stack.Protected>

            {/* Auth screens: logged out or unverified, plus a post-boot loading
                blip so the route does not remount on every session revalidate. */}
            <Stack.Protected guard={gate === 'auth' || isLoadingBlip}>
              <Stack.Screen name="(auth)" options={{ headerShown: false }} />
            </Stack.Protected>

            {/* Pre-app setup: owns the whole screen, so `(tabs)`/`(root)` cannot
                paint behind it (ADR 0006). */}
            <Stack.Protected guard={gate === 'setup'}>
              <Stack.Screen name="(setup)" options={{ headerShown: false }} />
            </Stack.Protected>

            {/* The app proper: verified, with nothing left to finish. */}
            <Stack.Protected guard={gate === 'app' || isLoadingBlip}>
              <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
              <Stack.Screen name="(root)" options={{ headerShown: false }} />
            </Stack.Protected>
          </Stack>
        </ResponsiveShell>
      </ResponsiveShellProvider>
    </>
  );
}

// Inner component that can access the theme
function AppContent() {
  const theme = useAppTheme();
  const colorScheme = useUnifiedColorScheme();

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      {/* KeyboardProvider drives keyboard-aware layout app-wide; must sit above
          every screen so KeyboardAwareScrollView / KeyboardStickyView can hook in. */}
      <KeyboardProvider>
        <QueryProvider>
          <AuthProvider>
            {/* Above LocationProvider on purpose: location acquisition waits on
                the same gate the routes do, so it has to be able to read it. */}
            <AppGateProvider>
              {/* Inside AuthProvider: location acquisition waits for verified auth,
                  so the OS prompt never appears on the login screen. */}
              <LocationProvider>
                <TourTargetProvider>
                  <ToastProvider>
                    <ConfirmationProvider>
                      <SafeAreaProvider initialMetrics={initialWindowMetrics}>
                        <ProtectedRoutes />
                        {/* Rendered last so it paints above the routes, covering
                            LoadingScreen's "Checking authentication..." on cold boot. */}
                        <SplashBridge />
                      </SafeAreaProvider>
                    </ConfirmationProvider>
                  </ToastProvider>
                </TourTargetProvider>
              </LocationProvider>
            </AppGateProvider>
          </AuthProvider>
        </QueryProvider>
      </KeyboardProvider>
      <StatusBar style={colorScheme === 'dark' ? 'light' : 'dark'} />
    </GestureHandlerRootView>
  );
}

// Main root layout component
function RootLayout() {
  const [loaded] = useFonts({
    HankenGrotesk_400Regular,
    HankenGrotesk_500Medium,
    HankenGrotesk_600SemiBold,
    HankenGrotesk_700Bold,
    "SpaceMono-Regular": require("@/assets/fonts/SpaceMono-Regular.ttf"),
    ...Ionicons.font,
  });

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();
    }
  }, [loaded]);

  if (!loaded) {
    return null;
  }

  return (
    <UnifiedThemeProvider>
      <AppContent />
    </UnifiedThemeProvider>
  );
}

export default sentryWrap(RootLayout);
